import ApiService from "@/core/services/ApiService";

/**
 * @description Cliente HTTP del módulo de Simulación de Pruebas Técnicas
 * (talento de ciberseguridad). Sigue la misma convención que el resto de
 * services (p.ej. TalentAssessmentService): wrapper delgado sobre
 * ApiService (axios + Bearer JWT vía ApiService.setHeader/JwtService), sin
 * lógica de estado — el estado vive en SkillsSimulationModule (Vuex).
 *
 * Contrato CONFIRMADO por el backend (repo SeccuriBackend,
 * SkillsSimulationController propio bajo api/skills-simulation, con
 * [Authorize(Roles = "Talent")] en toda la superficie desde el día uno):
 *  - POST /session no recibe body: el backend resuelve el talent desde el
 *    JWT, no desde un profileId enviado por el cliente. Es idempotente —
 *    un talent tiene una sola sesión de por vida **por skill** (ver nota
 *    de "skill" más abajo); si ya existe (en curso o completada), la
 *    devuelve tal cual en vez de crear una segunda.
 *  - GET /session/{sessionId}/menu permite reanudar/refrescar el estado de
 *    la sesión sin volver a llamar POST /session (útil p.ej. tras un
 *    reload de página en la vista de runner).
 *  - category, status y type viajan como string (nombre del enum, p.ej.
 *    "Triage", "Available", "Classify"), no como número.
 *  - GET /session/{sessionId} (sin sufijo "/summary") devuelve el resumen
 *    final; responde 400 (code SUMMARY_NOT_READY) mientras falte alguna
 *    prueba por aprobar.
 *  - El envelope de respuesta (Seccuri.Shared.Results.Result<T>) serializa
 *    como `{ data, errors: [{ error, code }], succeeded, message }` —
 *    `errors` es un array de objetos, no strings sueltos.
 *    SkillsSimulationModule.ts ya extrae `.error` de cada uno
 *    (extractErrorMessages) en vez de asumir strings.
 *
 * MULTI-SKILL (agregado para soportar más de un track evaluable, p.ej.
 * "SocAnalyst" y "CloudSecurity" — ver SkillsSimulationSkillsConfig.ts):
 * el catálogo de skills es config estática del frontend, pero CREAR la
 * sesión sí necesita que el backend sepa para qué skill es. Se envía como
 * query param `?skill=` en vez de agregar un body a POST /session (para no
 * romper el "body vacío" ya confirmado). PENDIENTE de que el backend lo
 * implemente — ver el prompt actualizado para la sesión de SeccuriBackend.
 * Una vez creada la sesión, sessionId ya identifica unívocamente el par
 * (talent, skill) — ningún otro endpoint necesita `skill` en el path.
 *
 * RETRY (reintentar el simulacro completo, agregado luego de que el
 * backend confirmó soporte para reintentos ilimitados por skill): la
 * unicidad de POST /session ya NO es "una sesión por (talent, skill)" a
 * secas — ahora, si la última sesión de ese (talent, skill) está
 * completa (allPassed), POST /session crea una sesión NUEVA e
 * independiente en vez de devolver la vieja; si está incompleta, sigue
 * siendo idempotente y la devuelve tal cual. Por eso START_SKILLS_SIMULATION
 * ya no se puede dispatch-ear a ciegas al entrar a una pantalla del
 * módulo (como se hacía antes) — primero hay que consultar
 * GET /skills-simulation/history?skill= para saber si el último intento
 * ya está completo, y solo entonces decidir entre reanudar (GET .../menu)
 * o crear uno nuevo (POST /session, y solo por acción explícita del
 * talento vía el botón "Intentar de nuevo"). Ver
 * ENSURE_SKILLS_SIMULATION_SESSION en SkillsSimulationModule.ts.
 */

const BASE_PATH = "skills-simulation";

export interface SubmitSkillsSimulationAnswerPayload {
  questionId: string;
  // Forma variable según el tipo de pregunta (Classify/MultipleChoiceSet/
  // Reorder/MultiSelect/SingleChoiceRubric) — ver SkillsSimulationModule.
  [key: string]: unknown;
}

export default class SkillsSimulationService {
  static startSession(skill: string) {
    // Body vacío a propósito: el backend identifica al talent por el JWT
    // (rol Talent), no por un profileId enviado desde el cliente. `skill`
    // va como query param (no en el body) — ver nota MULTI-SKILL arriba.
    return ApiService.post(`${BASE_PATH}/session`, undefined, {
      params: { skill },
    });
  }

  static getMenu(sessionId: string) {
    return ApiService.get(`${BASE_PATH}/session/${sessionId}`, "menu");
  }

  static getActiveQuestion(sessionId: string, category: string) {
    return ApiService.get(
      `${BASE_PATH}/session/${sessionId}/tests/${category}`,
      "question",
    );
  }

  static submitAnswer(
    sessionId: string,
    category: string,
    payload: SubmitSkillsSimulationAnswerPayload,
  ) {
    return ApiService.post(
      `${BASE_PATH}/session/${sessionId}/tests/${category}/submit`,
      payload,
    );
  }

  static getSummary(sessionId: string) {
    return ApiService.get(`${BASE_PATH}/session`, sessionId);
  }

  static getHistory(skill: string) {
    // Historial de intentos (completos e incompletos) del talent para
    // este skill, más reciente primero — ver nota RETRY más abajo.
    return ApiService.query(`${BASE_PATH}/history`, { params: { skill } });
  }
}
